import { Tab, Tabs } from '@heroui/react';
import type { EProductType } from '@quesmed/types/models';
// import { useFormContext } from 'react-hook-form';
import { cn } from '@/core/ui';
// import { useGlobal } from '@/store';
import { AiConfigLlmTab } from './ai-config-llm-tab';
import { AiConfigSttTab } from './ai-config-stt-tab';
import { AiConfigTtsTab } from './ai-config-tts-tab';
import { supportsReasoningEffort } from './llm-model-capabilities';
// import { isPacesProduct } from './product';
import type { IAgentConfig, IAiConfig, IAiConfigLlm, IAiConfigPhase } from './schema';

const PHASES = [
  { key: 'patient', label: 'Patient' },
  { key: 'examination', label: 'Exam' },
  // { key: 'turn_router', label: 'Router' },
  { key: 'examiner', label: 'Examiner' },
  { key: 'markscheme', label: 'Marks' },
  { key: 'result', label: 'Result' },
] as const;

type PhaseKey = (typeof PHASES)[number]['key'];
const STARTING_AGENTS = ['patient', 'examination', 'examiner'] as const;

export const DEFAULT_AGENT_CONFIG: Required<Pick<IAgentConfig, 'entryAgent' | 'multiAgent'>> = {
  entryAgent: 'patient',
  multiAgent: false,
};

const parseNumber = (value: string | undefined) => {
  if (!value) return undefined;
  if (typeof value === 'number') {
    return value;
  }
  if (!value?.trim()) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const parseCsv = (value: string | undefined) => {
  if (!value?.trim()) return undefined;
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length ? items : undefined;
};

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

const parseStartingAgent = (value: unknown): IAgentConfig['entryAgent'] => {
  if (typeof value !== 'string') return undefined;
  return STARTING_AGENTS.includes(value as (typeof STARTING_AGENTS)[number])
    ? (value as IAgentConfig['entryAgent'])
    : undefined;
};

const parseAgentConfigFromApi = (value: unknown): IAgentConfig => {
  if (!value || typeof value !== 'object') return { ...DEFAULT_AGENT_CONFIG };

  const source = value as Record<string, unknown>;
  return {
    entryAgent: parseStartingAgent(source.entryAgent) ?? DEFAULT_AGENT_CONFIG.entryAgent,
    multiAgent: parseBoolean(source.multiAgent) ?? DEFAULT_AGENT_CONFIG.multiAgent,
  };
};

const agentConfigToApi = (value: IAgentConfig | undefined): IAgentConfig => ({
  entryAgent: parseStartingAgent(value?.entryAgent) ?? DEFAULT_AGENT_CONFIG.entryAgent,
  multiAgent: parseBoolean(value?.multiAgent) ?? DEFAULT_AGENT_CONFIG.multiAgent,
});

const _toReasoningEffort = (value: unknown): IAiConfigLlm['reasoning_effort'] | undefined => {
  if (['low', 'medium', 'high', 'none', ' minimal'].includes(value as string))
    return value as IAiConfigLlm['reasoning_effort'];
  return undefined;
};

const removeEmpty = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(removeEmpty).filter((item) => item !== undefined);
  }
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, child]) => [key, removeEmpty(child)] as const)
      .filter(([, child]) => child !== undefined);
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  if (value === '' || value === null || value === undefined) return undefined;
  return value;
};

const mapPhaseFromApi = (phase: unknown): IAiConfigPhase | undefined => {
  if (!phase || typeof phase !== 'object') return undefined;
  const src = phase as Record<string, any>;
  const llm = src.llm || {};
  const stt = src.stt || {};
  const tts = src.tts || {};
  const deepgramStt = stt.deepgram || {};
  const openaiStt = stt.openai || {};
  const assemblyaiStt = stt.assemblyai || {};
  const xaiStt = stt.xai || {};

  const deepgramTts = tts.deepgram || {};
  const openaiTts = tts.openai || {};
  const minimaxTts = tts.minimax || {};
  const rimeTts = tts.rime || {};
  const inworldTts = tts.inworld || {};
  const xaiTts = tts.xai || {};
  const elevenlabsTts = tts.elevenlabs || {};
  const cartesiaTts = tts.cartesia || {};
  const fishaudioTts = tts.fishaudio || {};
  const gradiumTts = tts.gradium || {};
  const geminiTts = tts.gemini || {};
  return {
    llm: {
      model: llm.model ? String(llm.model) : '',
      temperature: llm.temperature !== undefined ? String(llm.temperature) : '',
      provider: llm.provider ? String(llm.provider) : '',
      reasoning_effort: llm.reasoning_effort as IAiConfigLlm['reasoning_effort'],
      reasoning_summary: llm.reasoning_summary as IAiConfigLlm['reasoning_summary'],
      max_completion_tokens: llm.max_completion_tokens !== undefined ? String(llm.max_completion_tokens) : '',
      promptId: llm.promptId != null && llm.promptId !== '' ? String(llm.promptId) : '',
      userPromptId: llm.userPromptId != null && llm.userPromptId !== '' ? String(llm.userPromptId) : '',
      transcriptMode:
        llm.transcriptMode === 'full' || llm.transcriptMode === 'user_only' ? llm.transcriptMode : undefined,
    },
    stt: {
      provider: stt.provider ? String(stt.provider) : '',
      deepgram: {
        model: deepgramStt.model ? String(deepgramStt.model) : '',
        language: deepgramStt.language ? String(deepgramStt.language) : '',
        smart_format: Boolean(deepgramStt.smart_format),
        interim_results: Boolean(deepgramStt.interim_results),
        enable_diarization: Boolean(deepgramStt.enable_diarization),
        endpointing: deepgramStt.endpointing !== undefined ? String(deepgramStt.endpointing) : '',
        keyterms: Array.isArray(deepgramStt.keyterms) ? deepgramStt.keyterms.join(', ') : '',
      },
      openai: {
        model: openaiStt.model ? String(openaiStt.model) : '',
        language: openaiStt.language ? String(openaiStt.language) : '',
        temperature: openaiStt.temperature !== undefined ? String(openaiStt.temperature) : '',
        prompt: openaiStt.prompt ? String(openaiStt.prompt) : '',
      },
      assemblyai: {
        model: assemblyaiStt.model ? String(assemblyaiStt.model) : '',
        language: assemblyaiStt.language ? String(assemblyaiStt.language) : '',
        speaker_labels: Boolean(assemblyaiStt.speaker_labels),
        keyterms_prompt: Array.isArray(assemblyaiStt.keyterms_prompt)
          ? assemblyaiStt.keyterms_prompt.join(', ')
          : '',
        min_turn_silence:
          assemblyaiStt.min_turn_silence !== undefined ? String(assemblyaiStt.min_turn_silence) : '',
        max_turn_silence:
          assemblyaiStt.max_turn_silence !== undefined ? String(assemblyaiStt.max_turn_silence) : '',
        vad_threshold: assemblyaiStt.vad_threshold !== undefined ? String(assemblyaiStt.vad_threshold) : '',
      },
      xai: {
        model: xaiStt.model ? String(xaiStt.model) : '',
        language: xaiStt.language ? String(xaiStt.language) : '',
        enable_diarization: Boolean(xaiStt.enable_diarization),
      },
    },
    tts: {
      provider: tts.provider ? String(tts.provider) : '',
      deepgram: {
        model: deepgramTts.model ? String(deepgramTts.model) : '',
        voice: deepgramTts.voice ? String(deepgramTts.voice) : '',
        speed: deepgramTts.speed ? String(deepgramTts.speed) : '',
      },
      openai: {
        model: openaiTts.model ? String(openaiTts.model) : '',
        voice: openaiTts.voice ? String(openaiTts.voice) : '',
        speed: openaiTts.speed ? String(openaiTts.speed) : '',
        instructions: openaiTts.instructions ? String(openaiTts.instructions) : '',
      },
      minimax: {
        model: minimaxTts.model ? String(minimaxTts.model) : '',
        voice: minimaxTts.voice ? String(minimaxTts.voice) : '',
        speed: minimaxTts.speed ? String(minimaxTts.speed) : '',
        pitch: minimaxTts.pitch ? String(minimaxTts.pitch) : '',
        vol: minimaxTts.vol ? String(minimaxTts.vol) : '',
        emotion: minimaxTts.emotion ? String(minimaxTts.emotion) : '',
      },
      rime: {
        model: rimeTts.model ? String(rimeTts.model) : '',
        voice: rimeTts.voice ? String(rimeTts.voice) : '',
        speed: rimeTts.speed ? String(rimeTts.speed) : '',
      },
      inworld: {
        model: inworldTts.model ? String(inworldTts.model) : '',
        voice: inworldTts.voice ? String(inworldTts.voice) : '',
        speaking_rate: inworldTts.speaking_rate ? String(inworldTts.speaking_rate) : '',
        temperature: inworldTts.temperature ? String(inworldTts.temperature) : '',
      },
      xai: {
        model: xaiTts.model ? String(xaiTts.model) : '',
        voice: xaiTts.voice ? String(xaiTts.voice) : '',
      },
      elevenlabs: {
        model: elevenlabsTts.model ? String(elevenlabsTts.model) : '',
        voice: elevenlabsTts.voice ? String(elevenlabsTts.voice) : '',
        stability: elevenlabsTts.stability ? String(elevenlabsTts.stability) : '',
        similarity_boost: elevenlabsTts.similarity_boost ? String(elevenlabsTts.similarity_boost) : '',
        style: elevenlabsTts.style ? String(elevenlabsTts.style) : '',
        speed: elevenlabsTts.speed ? String(elevenlabsTts.speed) : '',
        use_speaker_boost: elevenlabsTts.use_speaker_boost,
      },
      cartesia: {
        model: cartesiaTts.model ? String(cartesiaTts.model) : '',
        voice: cartesiaTts.voice ? String(cartesiaTts.voice) : '',
        speed: cartesiaTts.speed ? String(cartesiaTts.speed) : '',
        volume: cartesiaTts.volume ? String(cartesiaTts.volume) : '',
        emotion: cartesiaTts.emotion ? String(cartesiaTts.emotion) : '',
      },
      fishaudio: {
        model: fishaudioTts.model ? String(fishaudioTts.model) : '',
        voice: fishaudioTts.voice ? String(fishaudioTts.voice) : '',
        speed:
          fishaudioTts.speed !== undefined && fishaudioTts.speed !== null ? String(fishaudioTts.speed) : '',
        volume:
          fishaudioTts.volume !== undefined && fishaudioTts.volume !== null
            ? String(fishaudioTts.volume)
            : '',
        latency_mode: fishaudioTts.latency_mode ? String(fishaudioTts.latency_mode) : '',
      },
      gradium: {
        model: gradiumTts.model ? String(gradiumTts.model) : '',
        voice: gradiumTts.voice ? String(gradiumTts.voice) : '',
      },
      gemini: {
        model: geminiTts.model ? String(geminiTts.model) : '',
        voice: geminiTts.voice ? String(geminiTts.voice) : '',
      },
    },
  };
};

export const getAiConfigFormDefaults = (raw: unknown): IAiConfig | undefined => {
  if (!raw || typeof raw !== 'object') {
    return {
      agentConfig: { ...DEFAULT_AGENT_CONFIG },
    };
  }
  const source = raw as Record<string, unknown>;
  return {
    patient: mapPhaseFromApi(source.patient),
    // turn_router: mapPhaseFromApi(source.turn_router),
    examiner: mapPhaseFromApi(source.examiner),
    examination: mapPhaseFromApi(source.examination),
    markscheme: mapPhaseFromApi(source.markscheme),
    result: mapPhaseFromApi(source.result),
    agentConfig: parseAgentConfigFromApi(source.agentConfig),
  };
};

const mergeProviderSettings = <T extends object>(baseline?: T, current?: T): T | undefined => {
  if (!baseline && !current) return undefined;
  return { ...baseline, ...current } as T;
};

const mergeAiConfigPhase = (
  baseline?: IAiConfigPhase,
  current?: IAiConfigPhase,
): IAiConfigPhase | undefined => {
  if (!baseline && !current) return undefined;

  return {
    llm: { ...baseline?.llm, ...current?.llm },
    stt: {
      provider: current?.stt?.provider || baseline?.stt?.provider,
      deepgram: mergeProviderSettings(baseline?.stt?.deepgram, current?.stt?.deepgram),
      openai: mergeProviderSettings(baseline?.stt?.openai, current?.stt?.openai),
      assemblyai: mergeProviderSettings(baseline?.stt?.assemblyai, current?.stt?.assemblyai),
      xai: mergeProviderSettings(baseline?.stt?.xai, current?.stt?.xai),
    },
    tts: {
      provider: current?.tts?.provider || baseline?.tts?.provider,
      deepgram: mergeProviderSettings(baseline?.tts?.deepgram, current?.tts?.deepgram),
      openai: mergeProviderSettings(baseline?.tts?.openai, current?.tts?.openai),
      minimax: mergeProviderSettings(baseline?.tts?.minimax, current?.tts?.minimax),
      rime: mergeProviderSettings(baseline?.tts?.rime, current?.tts?.rime),
      inworld: mergeProviderSettings(baseline?.tts?.inworld, current?.tts?.inworld),
      xai: mergeProviderSettings(baseline?.tts?.xai, current?.tts?.xai),
      elevenlabs: mergeProviderSettings(baseline?.tts?.elevenlabs, current?.tts?.elevenlabs),
      cartesia: mergeProviderSettings(baseline?.tts?.cartesia, current?.tts?.cartesia),
      fishaudio: mergeProviderSettings(baseline?.tts?.fishaudio, current?.tts?.fishaudio),
      gradium: mergeProviderSettings(baseline?.tts?.gradium, current?.tts?.gradium),
      gemini: mergeProviderSettings(baseline?.tts?.gemini, current?.tts?.gemini),
    },
  };
};

/** Merges live form values with loaded defaults so unmounted tab panels are not lost on save. */
export const mergeAiConfigForSubmit = (
  current: IAiConfig | null | undefined,
  baseline: IAiConfig | null | undefined,
): IAiConfig | undefined => {
  if (!current && !baseline) return undefined;

  return {
    patient: mergeAiConfigPhase(baseline?.patient, current?.patient),
    // turn_router: mergeAiConfigPhase(baseline?.turn_router, current?.turn_router),
    examiner: mergeAiConfigPhase(baseline?.examiner, current?.examiner),
    examination: mergeAiConfigPhase(baseline?.examination, current?.examination),
    markscheme: mergeAiConfigPhase(baseline?.markscheme, current?.markscheme),
    result: mergeAiConfigPhase(baseline?.result, current?.result),
    agentConfig: agentConfigToApi({
      ...baseline?.agentConfig,
      ...current?.agentConfig,
    }),
  };
};

const mapPhaseForSubmit = (phase: IAiConfigPhase | undefined) => {
  if (!phase) return undefined;
  const supportsReasoning = supportsReasoningEffort(phase.llm?.model);

  const payload = {
    llm: {
      model: phase.llm?.model,
      provider: phase.llm?.provider,
      temperature: supportsReasoning ? undefined : parseNumber(phase.llm?.temperature),
      reasoning_effort: supportsReasoning ? phase.llm?.reasoning_effort : undefined,
      reasoning_summary: supportsReasoning ? phase.llm?.reasoning_summary : undefined,
      max_completion_tokens: parseNumber(phase.llm?.max_completion_tokens),
      promptId: phase.llm?.promptId?.trim() ? phase.llm.promptId : undefined,
      userPromptId: phase.llm?.userPromptId?.trim() ? phase.llm.userPromptId : undefined,
      transcriptMode: phase.llm?.transcriptMode || undefined,
    },
    stt: {
      provider: phase.stt?.provider,
      deepgram: {
        model: phase.stt?.deepgram?.model,
        language: phase.stt?.deepgram?.language,
        smart_format: phase.stt?.deepgram?.smart_format,
        interim_results: phase.stt?.deepgram?.interim_results,
        enable_diarization: phase.stt?.deepgram?.enable_diarization,
        endpointing: parseNumber(phase.stt?.deepgram?.endpointing),
        keyterms: parseCsv(phase.stt?.deepgram?.keyterms),
      },
      openai: {
        model: phase.stt?.openai?.model,
        language: phase.stt?.openai?.language,
        temperature: parseNumber(phase.stt?.openai?.temperature),
        prompt: phase.stt?.openai?.prompt,
      },
      assemblyai: {
        model: phase.stt?.assemblyai?.model,
        language: phase.stt?.assemblyai?.language,
        speaker_labels: phase.stt?.assemblyai?.speaker_labels,
        keyterms_prompt: parseCsv(phase.stt?.assemblyai?.keyterms_prompt),
        min_turn_silence: parseNumber(phase.stt?.assemblyai?.min_turn_silence),
        max_turn_silence: parseNumber(phase.stt?.assemblyai?.max_turn_silence),
        vad_threshold: parseNumber(phase.stt?.assemblyai?.vad_threshold),
      },
      xai: {
        model: phase.stt?.xai?.model,
        language: phase.stt?.xai?.language,
        enable_diarization: phase.stt?.xai?.enable_diarization,
      },
    },
    tts: {
      provider: phase.tts?.provider,
      deepgram: {
        model: phase.tts?.deepgram?.model,
        voice: phase.tts?.deepgram?.voice,
        speed: phase.tts?.deepgram?.speed ? parseNumber(phase.tts?.deepgram?.speed) : undefined,
      },
      openai: {
        model: phase.tts?.openai?.model,
        voice: phase.tts?.openai?.voice,
        speed: phase.tts?.openai?.speed ? parseNumber(phase.tts?.openai?.speed) : undefined,
        instructions: phase.tts?.openai?.instructions ? String(phase.tts?.openai?.instructions) : undefined,
      },
      minimax: {
        model: phase.tts?.minimax?.model,
        voice: phase.tts?.minimax?.voice,
        speed: phase.tts?.minimax?.speed ? parseNumber(phase.tts?.minimax?.speed) : undefined,
        pitch: phase.tts?.minimax?.pitch ? parseNumber(phase.tts?.minimax?.pitch) : undefined,
        vol: phase.tts?.minimax?.vol ? parseNumber(phase.tts?.minimax?.vol) : undefined,
        emotion: phase.tts?.minimax?.emotion ? String(phase.tts?.minimax?.emotion) : undefined,
      },
      rime: {
        model: phase.tts?.rime?.model,
        voice: phase.tts?.rime?.voice,
        speed: phase.tts?.rime?.speed ? parseNumber(phase.tts?.rime?.speed) : undefined,
      },
      inworld: {
        model: phase.tts?.inworld?.model,
        voice: phase.tts?.inworld?.voice,
        speaking_rate: phase.tts?.inworld?.speaking_rate
          ? parseNumber(phase.tts?.inworld?.speaking_rate)
          : undefined,
        temperature: phase.tts?.inworld?.temperature
          ? parseNumber(phase.tts?.inworld?.temperature)
          : undefined,
      },
      xai: {
        model: phase.tts?.xai?.model,
        voice: phase.tts?.xai?.voice,
      },
      elevenlabs: {
        model: phase.tts?.elevenlabs?.model,
        voice: phase.tts?.elevenlabs?.voice,
        stability: phase.tts?.elevenlabs?.stability
          ? parseNumber(phase.tts?.elevenlabs?.stability)
          : undefined,
        similarity_boost: phase.tts?.elevenlabs?.similarity_boost
          ? parseNumber(phase.tts?.elevenlabs?.similarity_boost)
          : undefined,
        style: phase.tts?.elevenlabs?.style ? parseNumber(phase.tts?.elevenlabs?.style) : undefined,
        speed: phase.tts?.elevenlabs?.speed ? parseNumber(phase.tts?.elevenlabs?.speed) : undefined,
        use_speaker_boost: phase.tts?.elevenlabs?.use_speaker_boost,
      },
      cartesia: {
        model: phase.tts?.cartesia?.model,
        voice: phase.tts?.cartesia?.voice,
        speed: phase.tts?.cartesia?.speed ? parseNumber(phase.tts?.cartesia?.speed) : undefined,
        volume: phase.tts?.cartesia?.volume ? parseNumber(phase.tts?.cartesia?.volume) : undefined,
        emotion: phase.tts?.cartesia?.emotion ? String(phase.tts?.cartesia?.emotion) : undefined,
      },
      fishaudio: {
        model: phase.tts?.fishaudio?.model,
        voice: phase.tts?.fishaudio?.voice,
        speed: parseNumber(phase.tts?.fishaudio?.speed),
        volume: parseNumber(phase.tts?.fishaudio?.volume),
        latency_mode: phase.tts?.fishaudio?.latency_mode || undefined,
      },
      gradium: {
        model: phase.tts?.gradium?.model,
        voice: phase.tts?.gradium?.voice,
      },
      gemini: {
        model: phase.tts?.gemini?.model,
        voice: phase.tts?.gemini?.voice,
      },
    },
  };

  return removeEmpty(payload);
};

export const getAiConfigSubmitValue = (value: IAiConfig | null | undefined) => {
  if (!value) return undefined;
  const payload = {
    patient: mapPhaseForSubmit(value.patient),
    examination: mapPhaseForSubmit(value.examination),
    // turn_router: mapPhaseForSubmit(value.turn_router),
    examiner: mapPhaseForSubmit(value.examiner),
    markscheme: mapPhaseForSubmit(value.markscheme),
    result: mapPhaseForSubmit(value.result),
    agentConfig: agentConfigToApi(value.agentConfig),
  };

  return removeEmpty(payload);
};

const PHASE_TABS: Record<PhaseKey, ReadonlyArray<'llm' | 'stt' | 'tts'>> = {
  patient: ['stt', 'llm', 'tts'],
  examination: ['stt', 'llm', 'tts'],
  // turn_router: ['llm'],
  examiner: ['stt', 'llm', 'tts'],
  markscheme: ['llm'],
  result: ['llm'],
};

const PhaseSection = ({ phase }: { phase: PhaseKey }) => {
  const baseName = `aiConfig.${phase}` as const;
  const tabs = PHASE_TABS[phase];

  return (
    <Tabs aria-label={`${phase} config tabs`} variant='solid' destroyInactiveTabPanel={false}>
      {tabs.map((tab) => {
        if (tab === 'stt') {
          return (
            <Tab key='stt' title='STT'>
              <AiConfigSttTab baseName={baseName} />
            </Tab>
          );
        }
        if (tab === 'llm') {
          return (
            <Tab key='llm' title='LLM'>
              <AiConfigLlmTab baseName={baseName} phaseKey={phase} />
            </Tab>
          );
        }
        if (tab === 'tts') {
          return (
            <Tab key='tts' title='TTS'>
              <AiConfigTtsTab baseName={baseName} />
            </Tab>
          );
        }
        return null;
      })}
    </Tabs>
  );
};

export const AiConfigForm = ({ className }: { className?: string; productId?: EProductType }) => {
  // const dbType = useGlobal.use.dbType();
  // const { watch } = useFormContext<IStationSchema>();
  // const osceTypeId = watch('osceTypeId');
  // const isPaces = isPacesProduct(productId, dbType);
  // const showExtendedPhases = isPaces ? osceTypeId === EPaceType.CONSULTATION : osceTypeId === EOsceType.MIXED;
  // const visiblePhases = PHASES.filter((phase) => showExtendedPhases || phase.key !== 'examination');

  return (
    <section className={cn('rounded-md border border-default-200 p-4', className)}>
      <p className='text-sm font-semibold mb-3'>AI Config</p>
      <Tabs
        aria-label='AI config phases'
        variant='underlined'
        destroyInactiveTabPanel={false}
        className='w-full overflow-y-auto'
      >
        {PHASES.map((phase) => (
          <Tab key={phase.key} title={phase.label}>
            <div className='pt-3'>
              <PhaseSection phase={phase.key} />
            </div>
          </Tab>
        ))}
      </Tabs>
    </section>
  );
};
